<template>
  <section>
    <h2 class="text-xl md:text-2xl font-bold mb-4 md:mb-6">短篇小说创作</h2>

    <div class="flex mb-4 border-b border-gray-200">
      <button
        class="tab-btn px-4 md:px-6 py-2 rounded-t-md"
        :class="{ 'tab-active': textType === 'outline' }"
        @click="handleChangeTextType('outline')"
      >
        梗概
      </button>
      <button
        class="tab-btn px-4 md:px-6 py-2 rounded-t-md"
        :class="{ 'tab-active': textType === 'outlinetab' }"
        @click="handleChangeTextType('outlinetab')"
      >
        大纲
      </button>
      <button
        class="tab-btn px-4 md:px-6 py-2 rounded-t-md"
        :class="{ 'tab-active': textType === 'original' }"
        @click="handleChangeTextType('original')"
      >
        原文
      </button>
    </div>

    <div class="flex mb-6 border-b border-gray-200">
      <button
        class="px-4 md:px-6 py-2 rounded-t-md"
        :class="{ 'tab-active': inputMode === 'text' }"
        @click="handleChangeInputMode('text')"
      >
        输入文本
      </button>
      <button
        class="px-4 md:px-6 py-2 rounded-t-md"
        :class="{ 'tab-active': inputMode === 'file' }"
        @click="handleChangeInputMode('file')"
      >
        上传文件
      </button>
    </div>

    <div v-show="inputMode === 'text'" class="mb-6">
      <div v-show="textType === 'outline'" class="w-full">
        <textarea v-model="outline" class="w-full bg-white rounded-md p-3 resize-y min-h-[200px] focus:outline-none focus:ring-1 focus:ring-primary border border-gray-200" placeholder="请输入小说梗概（核心剧情+人物设定，最多30000字符）..."></textarea>
        <p class="text-textSub text-sm mt-2">当前字符数：{{ outline.length }}/30000</p>
      </div>
      <div v-show="textType === 'outlinetab'" class="w-full">
        <textarea v-model="outlineTab" class="w-full bg-white rounded-md p-3 resize-y min-h-[200px] focus:outline-none focus:ring-1 focus:ring-primary border border-gray-200" placeholder="请输入小说大纲（章节结构+剧情脉络，最多30000字符）..."></textarea>
        <p class="text-textSub text-sm mt-2">当前字符数：{{ outlineTab.length }}/30000</p>
      </div>
      <div v-show="textType === 'original'" class="w-full">
        <textarea v-model="original" class="w-full bg-white rounded-md p-3 resize-y min-h-[200px] focus:outline-none focus:ring-1 focus:ring-primary border border-gray-200" placeholder="请输入小说原文（完整文本/片段内容，最多30000字符）..."></textarea>
        <p class="text-textSub text-sm mt-2">当前字符数：{{ original.length }}/30000</p>
      </div>
    </div>

    <div v-show="inputMode === 'file'" class="mb-6">
      <div class="w-full bg-white rounded-md p-8 flex flex-col items-center justify-center min-h-[200px] border-2 border-dashed border-gray-200 hover:border-primary transition-colors">
        <i class="fa-solid fa-upload text-4xl text-textSub mb-4"></i>
        <p class="text-textSub mb-4">点击或拖拽文件至此处上传</p>
        <p class="text-xs text-textSub">支持格式：TXT（最大30000字符）</p>
        <input ref="fileInput" type="file" class="hidden" accept=".txt" @change="onFileChange">
        <button class="mt-4 bg-primary text-white px-4 py-2 rounded-md btn-hover" @click="$refs.fileInput.click()">选择文件</button>
      </div>
      <div v-if="selectedFile" class="mt-3 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 flex items-center justify-between text-xs md:text-sm text-textSub">
        <div class="flex items-center gap-2">
          <i class="fa-solid fa-file-lines text-textSub"></i>
          <span class="break-all">{{ selectedFile.name }}</span>
        </div>
        <button
          type="button"
          class="text-primary hover:underline ml-3 whitespace-nowrap"
          @click="clearSelectedFile"
        >
          删除并重新选择
        </button>
      </div>
    </div>

    <div v-show="!resultVisible" class="mb-6">
      <button
        type="button"
        class="w-full bg-primary text-white py-3 rounded-md text-lg btn-hover inline-flex items-center justify-center gap-2 flex-wrap"
        @click="startCreate"
      >
        <span>开始创作</span>
        <i class="fa-solid fa-bolt shrink-0 opacity-95" aria-hidden="true"></i>
        <span v-if="coinCost != null" class="text-sm font-normal opacity-90">{{ coinCost }}</span>
      </button>
    </div>
    <div v-show="resultVisible" class="flex gap-4 mb-6">
      <button
        class="flex-1 bg-primary text-white py-3 rounded-md text-lg btn-hover disabled:opacity-60 disabled:cursor-not-allowed"
        type="button"
        :disabled="signSubmitting || pushSubmitting"
        @click="handleSign"
      >
        {{ signSubmitting ? '提交中...' : '一键签约' }}
      </button>
      <button
        class="flex-1 bg-white border border-primary text-primary py-3 rounded-md text-lg btn-hover disabled:opacity-60 disabled:cursor-not-allowed"
        type="button"
        :disabled="signSubmitting || pushSubmitting"
        @click="handlePush"
      >
        {{ pushSubmitting ? '发布中...' : '发布到简书' }}
      </button>
    </div>

    <div v-show="resultVisible" class="bg-white rounded-md p-6 border border-gray-200">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-xl font-semibold">创作结果</h3>
        <button class="copy-btn" @click="$emit('copy', { workName: workName, introList: introList, shortNovelContent: shortNovelContent })">
          <i class="fa-solid fa-copy mr-1"></i>一键复制
        </button>
      </div>
      <div v-if="workName" class="text-sm text-textSub mb-3">
        作品名称：{{ workName }}
      </div>
      <div class="mb-4">
        <h4 class="font-medium mb-2">导语推荐：</h4>
        <p v-if="!introList" class="text-textSub mb-4">暂无导语推荐，请查看下方小说内容。</p>
        <div v-else class="text-textSub whitespace-pre-line mb-4">{{ introList }}</div>
      </div>
      <div>
        <h4 class="font-medium mb-2">小说内容：</h4>
        <div class="text-textSub whitespace-pre-line mb-4">{{ shortNovelContent }}</div>
      </div>
    </div>

    <!-- 开始创作：填写作品名称 -->
    <div
      v-if="signModalVisible"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="closeSignModal"
    >
      <div class="bg-white rounded-lg p-6 w-full max-w-md border border-gray-200 shadow-lg mx-4">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-semibold">开始创作</h3>
          <button class="text-textSub text-xl cursor-pointer" @click="closeSignModal">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="mb-5">
          <label class="block text-sm text-textSub mb-1">作品名称</label>
          <input
            v-model="signWorkName"
            type="text"
            class="w-full border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder="请输入作品名称"
          >
        </div>
        <div class="flex gap-3">
          <button
            class="flex-1 border border-gray-300 text-textMain py-2 rounded-md btn-hover"
            type="button"
            @click="closeSignModal"
          >
            取消
          </button>
          <button
            class="flex-1 bg-primary text-white py-2 rounded-md btn-hover inline-flex items-center justify-center gap-2 flex-wrap"
            type="button"
            :disabled="signSubmitting"
            @click="confirmWorkNameAndCreate"
          >
            <template v-if="signSubmitting">处理中...</template>
            <template v-else>
              <span>确认并开始创作</span>
              <i class="fa-solid fa-bolt shrink-0 opacity-95" aria-hidden="true"></i>
              <span v-if="coinCost != null" class="text-sm font-normal opacity-90">{{ coinCost }}</span>
            </template>
          </button>
        </div>
      </div>
    </div>

    <div class="guide-module">
      <h3 class="guide-title">
        <i class="fa-solid fa-lightbulb guide-icon"></i>
        短篇小说创作使用攻略
      </h3>
      <div class="guide-list">
        <div v-for="(item, index) in guideList" :key="index" class="guide-item">
          <i class="fa-solid fa-check-circle guide-icon"></i>
          <span>{{ item }}</span>
        </div>
      </div>
    </div>

    <!-- 创作中浮层提示 -->
    <div
      v-if="creating"
      class="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded-lg px-6 py-5 shadow-md border border-gray-200 max-w-sm w-full mx-4">
        <div class="flex items-center justify-center gap-2 mb-4">
          <i class="fa-solid fa-spinner fa-spin text-primary"></i>
          <div class="text-primary text-lg font-semibold">正在为你创作小说...</div>
        </div>
        <div class="space-y-2 text-sm">
          <div
            v-for="(s, idx) in creatingSteps"
            :key="s.key"
            class="flex items-start gap-2 text-textMain"
          >
            <div class="mt-0.5">
              <i
                v-if="getStepStatus(idx) === 'FAILED'"
                class="fa-solid fa-circle-xmark text-red-500"
              ></i>
              <i
                v-else-if="getStepStatus(idx) === 'SUCCESS'"
                class="fa-solid fa-circle-check text-green-500"
              ></i>
              <i
                v-else
                class="fa-solid fa-circle text-gray-300"
              ></i>
            </div>
            <div class="flex-1">
              <div class="grid grid-cols-[1fr_12rem] items-center gap-3 leading-6">
                <span class="font-medium">{{ s.label }}</span>
                <span class="text-textSub whitespace-nowrap text-right">预计{{ s.etaMin }}分钟，请耐心等待</span>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-4 text-xs text-textSub leading-relaxed">
          如刷新页面请到个人中心我的资产中查看
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { createShortNovelByText, uploadShortNovelFile, queryShortNovelTask, signShortNovel, pushShortNovel } from '../api/shortNovel'
import coinCostActive from '../mixins/coinCostActive'
import { poll } from '../utils/poll'
import { readTextFileWithLimit } from '../utils/uploadValidator'

var DEFAULT_GUIDE = [
  '输入文本时明确故事类型和核心人物，生成效果更佳',
  '上传文件建议分段整理，避免大段无结构文本',
  '梗概模式适合快速生成故事框架，原文模式适合细节创作',
  '每月会员点数优先用于长篇小说创作，性价比更高',
  '生成结果可多次调整关键词，直到符合预期风格',
  '签约前建议完善小说细节，提高审核通过率'
]

export default {
  name: 'ShortNovel',
  coinCostKey: 'shortNovel',
  mixins: [coinCostActive],
  data () {
    return {
      textType: 'outline',
      inputMode: 'text',
      outline: '',
      outlineTab: '',
      original: '',
      resultVisible: false,
      shortNovelContent: '',
      introList: '',
      guideList: DEFAULT_GUIDE.slice(),
      creating: false,
      creatingLastResult: null,
      selectedFile: null,
      taskId: '',
      workName: '',
      signModalVisible: false,
      signWorkName: '',
      signSubmitting: false,
      pushSubmitting: false
    }
  },
  computed: {
    creatingSteps () {
      return [
        { key: '1', label: '梳理小说大纲', etaMin: 1 },
        { key: '2', label: '行文模板匹配', etaMin: 1 },
        { key: '3', label: '生成小说', etaMin: 4 },
        { key: '4', label: '生成导语', etaMin: 1 }
      ]
    }
  },
  methods: {
    resetCreatingProgress () {
      this.creatingLastResult = null
    },
    stopCreatingProgress () {
      // no-op (只依赖 pollTaskResult 返回值推进步骤)
    },
    getStepStatus (idx) {
      const res = this.creatingLastResult || ''
      if (!res) return ''
      const n = idx + 1
      const statusKey = 'step_' + n + '_status'
      const direct = res && res[statusKey]
      if (direct === 'SUCCESS' || direct === 'FAILED') return direct
      const progress = res && res.taskProgress
      if (typeof progress === 'string' && progress.indexOf('=') > -1) {
        const parts = progress.split('=').map(s => s.trim())
        if (parts[0] === String(n) && (parts[1] === 'SUCCESS' || parts[1] === 'FAILED')) return parts[1]
      }
      return ''
    },
    handleChangeTextType (type) {
      if (this.resultVisible) this.clearResult()
      this.textType = type
    },
    handleChangeInputMode (mode) {
      if (this.resultVisible) this.clearResult()
      this.inputMode = mode
    },
    handleSign () {
      if (this.signSubmitting || this.pushSubmitting) return
      const taskId = this.taskId
      if (!taskId) {
        this.$emit('show-modal', '提示', '请先完成创作后再进行签约。')
        return
      }
      this.signSubmitting = true
      signShortNovel({ taskId })
        .then(() => {
          this.signSubmitting = false
          this.$emit('show-modal', '签约提示', '签约申请已提交，请等待平台审核！')
          this.clearResult()
        })
        .catch((err) => {
          this.signSubmitting = false
          const msg = (err && err.message) || '签约请求失败，请稍后重试。'
          this.$emit('show-modal', '签约失败', msg)
        })
    },
    closeSignModal () {
      this.signModalVisible = false
      this.signWorkName = ''
      this.signSubmitting = false
    },
    async confirmWorkNameAndCreate () {
      if (this.signSubmitting || this.pushSubmitting || this.creating) return
      const workName = (this.signWorkName || '').trim()
      if (!workName) {
        this.$emit('show-modal', '提示', '请输入作品名称。')
        return
      }
      this.workName = workName
      this.signModalVisible = false
      this.signWorkName = ''
      await this.doCreate()
    },
    async handlePush () {
      if (this.pushSubmitting || this.signSubmitting) return
      const taskId = this.taskId
      if (!taskId) {
        this.$emit('show-modal', '发布失败', '请先完成创作后再发布到简书。')
        return
      }
      this.pushSubmitting = true
      try {
        await pushShortNovel({ taskId })
        this.$emit('show-modal', '发布提示', '作品已成功发布到简书平台！')
      } catch (err) {
        const msg = (err && err.message) || '发布请求失败，请稍后重试。'
        this.$emit('show-modal', '发布失败', msg)
      } finally {
        this.pushSubmitting = false
      }
    },
    async startCreate () {
      if (this.creating) return
      this.signModalVisible = true
    },
    async doCreate () {
      if (this.inputMode === 'text') {
        let content = ''
        if (this.textType === 'outline') {
          content = this.outline
        } else if (this.textType === 'outlinetab') {
          content = this.outlineTab
        } else if (this.textType === 'original') {
          content = this.original
        }
        if (!content || !content.trim()) {
          this.$emit('show-modal', '提示', '请输入要创作的文本内容。')
          return
        }
        this.resetCreatingProgress()
        this.creating = true
        try {
          const res = await createShortNovelByText({
            textType: this.textType,
            content,
            workName: this.workName || ''
          })
          const taskId = (res && (res.taskId != null ? res.taskId : (res.data && res.data.taskId))) || ''
          if (!taskId) {
            this.creating = false
            this.$emit('show-modal', '创作失败', '未返回任务ID，请稍后重试。')
            return
          }
          this.taskId = String(taskId)
          const data = await this.pollTaskResult(taskId)
          if (data) {
            this.shortNovelContent = data.text || '创作结果为空，请稍后重试或调整输入内容。'
            this.introList = data.introList || '创作结果为空，请稍后重试或调整输入内容。'
            this.resultVisible = true
          }
        } catch (err) {
          const msg = (err && err.message) || '文章设定出现问题，请修改后重新提交。'
          this.$emit('show-modal', '创作失败', msg)
        } finally {
          this.creating = false
          this.stopCreatingProgress()
        }
      } else if (this.inputMode === 'file') {
        if (!this.selectedFile) {
          this.$emit('show-modal', '提示', '请先选择要上传的文件。')
          return
        }
        const formData = new FormData()
        formData.append('textType', this.textType)
        formData.append('workName', this.workName || '')
        formData.append('inputMode', 'file')
        formData.append('file', this.selectedFile)
        this.resetCreatingProgress()
        this.creating = true
        try {
          const res = await uploadShortNovelFile(formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
          const taskId = (res && (res.taskId != null ? res.taskId : (res.data && res.data.taskId))) || ''
          if (!taskId) {
            this.creating = false
            this.$emit('show-modal', '创作失败', '未返回任务ID，请稍后重试。')
            return
          }
          this.taskId = String(taskId)
          const data = await this.pollTaskResult(taskId)
          if (data) {
            let txt = ''
            let introStr = ''
            if (data && typeof data.content === 'string') {
              txt = data.content
            } else if (data && typeof data.text === 'string') {
              txt = data.text
            } else if (typeof data === 'string') {
              txt = data
            }
            if (data) {
              introStr = (data.introList != null ? String(data.introList) : (data.leadList != null ? String(data.leadList) : '')) || ''
            }
            this.shortNovelContent = txt || '创作结果为空，请稍后重试或更换文件。'
            this.introList = introStr
            this.resultVisible = true
          }
        } catch (err) {
          const msg = (err && err.message) || '文件上传或处理失败，请稍后重试。'
          this.$emit('show-modal', '创作失败', msg)
        } finally {
          this.creating = false
          this.stopCreatingProgress()
        }
      }
    },
    pollTaskResult (taskId) {
      return poll(function () {
        return queryShortNovelTask(taskId)
      }, {
        onResult: (res) => {
          this.creatingLastResult = res || null
        },
        shouldStop: function (res) {
          return res && res.taskStatus === 'SUCCESS'
        },
        isError: function (res) {
          if (res && res.taskStatus === 'FAILED') {
            const msg = (res && (res.message || res.msg)) || '任务执行失败,不扣除点数'
            return new Error(msg)
          }
          return null
        },
        interval: 15000,
        maxTimes: 150
      })
    },
    clearResult () {
      this.outline = ''
      this.outlineTab = ''
      this.original = ''
      this.resultVisible = false
      this.textType = 'outline'
      this.inputMode = 'text'
      this.shortNovelContent = ''
      this.introList = ''
      this.selectedFile = null
      this.taskId = ''
      this.workName = ''
      this.signModalVisible = false
      this.signWorkName = ''
      this.signSubmitting = false
      this.pushSubmitting = false
    },
    async onFileChange (e) {
      const files = e && e.target && e.target.files
      if (!files || !files[0]) return
      const file = files[0]

      try {
        const info = await readTextFileWithLimit(file, { maxLength: 30000 })
        this.selectedFile = file
        this.$emit('show-modal', '文件选择成功', '已选择文件：' + file.name + '\n当前字数：' + info.length + '（上限 30000 字）')
      } catch (err) {
        this.selectedFile = null
        if (this.$refs.fileInput && this.$refs.fileInput.value) {
          this.$refs.fileInput.value = ''
        }
        const msg = err && err.message ? err.message : '文件读取失败，请稍后重试或更换文件。'
        this.$emit('show-modal', err && err.code === 'FILE_TOO_LARGE' ? '文件过大' : '文件读取失败', msg)
      }
    },
    clearSelectedFile () {
      this.selectedFile = null
      if (this.$refs.fileInput && this.$refs.fileInput.value) {
        this.$refs.fileInput.value = ''
      }
    }
  }
}
</script>
